<script setup>
import { HrBtn } from '../primitives';

/**
 * Destructive action button — highlights the error color role to signal irreversibility.
 *
 * @status recommended
 * @group actions
 * @useWhen Permanently deleting, removing, or resetting data
 * @avoidWhen The action can be undone — use HrConfirmBtn
 * @related HrConfirmBtn
 * @a11y Native button; activates on Enter and Space.
 */
defineOptions({ name: 'HrDestructiveBtn' });

const emit = defineEmits({
  /** Fired when the user activates the button. */
  click: null,
});

defineProps({
  /**
   * Button text label. When null the default slot provides the content.
   * @replacesSlot default
   */
  label: {
    type: String,
    default: null,
  },
  /** Icon displayed to the left of the label. */
  icon: {
    type: String,
    default: null,
  },
  /** Prevents interaction and applies a dimmed appearance. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Native button type. Defaults to `button` to prevent accidental form submission.
   * @values button, submit, reset
   */
  type: {
    type: String,
    default: 'button',
    validator: (v) => ['button', 'submit', 'reset'].includes(v),
  },
  /** Renders a pill-shaped skeleton placeholder instead of the button. */
  pending: {
    type: Boolean,
    default: false,
  },
  /** Vue Router route location. Renders the button as a router-link. */
  to: {
    type: [String, Object],
    default: null,
  },
  /** Replace the current history entry instead of pushing a new one. */
  replace: {
    type: Boolean,
    default: false,
  },
  /** Native anchor href. Use for external links; prefer `to` for in-app navigation. */
  href: {
    type: String,
    default: null,
  },
  /** Native anchor target (e.g. `_blank`). Only meaningful when `href` is set. */
  target: {
    type: String,
    default: null,
  },
});

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = { flat: true, color: 'negative' };
</script>
<template>
  <hr-btn
    variant="text"
    destructive
    :legacy-props="legacyProps"
    v-bind="$props"
    @click="emit('click', $event)"
  >
    <!-- @slot Button label / content. -->
    <slot />
  </hr-btn>
</template>
